import { useMemo } from "react";

import { useSelectionStore } from "../../state/useSelectionStore";
import {
  useActiveScenario,
  useScenarioStore,
  type ScenarioArea,
} from "../../state/useScenarioStore";
import { useAreaScenario } from "../../lib/useScenario";
import { euro, euroShort, pct, pricePerM2 } from "../../lib/format";
import { levelDef } from "../../map/levels";
import { ScenarioList } from "./ScenarioList";
import { ScenarioInputs } from "./ScenarioInputs";
import { ProjectionChart } from "./ProjectionChart";
import { PinnedAreas } from "./PinnedAreas";

/**
 * Price a hypothetical flat anywhere on the map, and imagine it forward.
 *
 * Clicking an area prices it under every scenario at once — the list is the
 * comparison — while the block below is the active scenario in full, with the
 * quartile range and the chart a one-line row cannot carry.
 *
 * Nothing here is a forecast. It answers "if this area keeps doing what it has
 * been doing, what would a flat this size be worth in N years" — a question
 * with an arithmetic answer, unlike "what will happen to the market".
 */
export function ScenarioPanel({ visible }: { visible: boolean }) {
  const selection = useSelectionStore((s) => s.selection);
  const scenario = useActiveScenario();
  const addArea = useScenarioStore((s) => s.addArea);
  const removeArea = useScenarioStore((s) => s.removeArea);

  const area: ScenarioArea | null = useMemo(
    () =>
      selection
        ? {
            scope: selection.scope,
            code: selection.code,
            name: selection.name,
            lon: selection.point.lon,
            lat: selection.point.lat,
          }
        : null,
    [selection],
  );

  const { projection, fit, rate, isLoading } = useAreaScenario(area, scenario);
  const inScenario =
    area !== null &&
    scenario.areas.some((a) => a.scope === area.scope && a.code === area.code);

  return (
    <div className="px-4 py-3">
      {area === null ? (
        <p className="mb-3 rounded-lg bg-surface-alt px-3 py-3 text-center text-[12px] leading-snug text-ink-soft">
          Click an area on the map.
        </p>
      ) : (
        <header className="mb-3 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-[13px] font-semibold">{area.name}</h3>
            <p className="text-[10px] text-ink-soft">
              {levelDef(area.scope).noun}
              {projection &&
                ` · ${pricePerM2(projection.base.perM2)} in ${projection.base.year}`}
            </p>
          </div>
          <button
            onClick={() =>
              inScenario ? removeArea(area.scope, area.code) : addArea(area)
            }
            className={`shrink-0 rounded-md border px-2 py-1 text-[10px] font-medium transition-colors ${
              inScenario
                ? "border-accent text-accent"
                : "border-line text-ink-soft hover:border-accent hover:text-accent"
            }`}
            title={`${inScenario ? "Remove from" : "Add to"} ${scenario.name}`}
          >
            {inScenario ? "Added" : "+ Add"}
          </button>
        </header>
      )}

      <ScenarioList area={area} />

      <div className="mt-4 border-t border-line pt-3">
        <ScenarioInputs historicRate={fit?.rate ?? null} />
      </div>

      {area !== null && (
        <div className="mt-4 border-t border-line pt-3">
          <h3 className="mb-2 text-[10px] font-medium uppercase tracking-wider text-ink-soft">
            {scenario.name} in detail
          </h3>

          {isLoading ? (
            <div className="h-32 animate-pulse rounded-lg bg-surface-alt" />
          ) : !projection ? (
            <p className="text-[12px] leading-snug text-ink-soft">
              Fewer than five recorded sales here — no median to build on.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Figure
                  label={`Today (${projection.base.year})`}
                  value={euro(projection.base.value)}
                  sub={`${euroShort(projection.base.low)} – ${euroShort(projection.base.high)} middle half`}
                />
                <Figure
                  label={`In ${scenario.horizon} year${scenario.horizon === 1 ? "" : "s"}`}
                  value={euro(projection.final)}
                  sub={`${euroShort(projection.finalLow)} – ${euroShort(projection.finalHigh)}`}
                  accent
                />
              </div>

              <div className="mt-2 flex items-baseline gap-2 rounded-lg bg-surface-alt px-2.5 py-1.5">
                <span
                  className={`text-[13px] font-semibold tabular-nums ${
                    projection.final >= projection.base.value
                      ? "text-[#2e7d32]"
                      : "text-[#cf3a2e]"
                  }`}
                >
                  {projection.final >= projection.base.value ? "+" : "−"}
                  {euroShort(Math.abs(projection.final - projection.base.value))}
                </span>
                <span className="text-[11px] leading-snug text-ink-soft">
                  by {projection.finalYear}, at {pct(rate)}/yr
                </span>
              </div>

              {/* Recharts measures its container, and a pane hidden behind
                  another tab measures zero — it would warn on every render and
                  draw nothing. The rows it needs are already computed. */}
              {visible && (
                <div className="mt-3">
                  <ProjectionChart projection={projection} />
                </div>
              )}

              {Math.abs(projection.base.typicalSurface - scenario.surface) >
                25 && (
                <p className="mt-1.5 text-[10px] leading-snug text-ink-soft">
                  Flats here are typically{" "}
                  {Math.round(projection.base.typicalSurface)} m², so the median
                  fits {scenario.surface} m² less well.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <PinnedAreas />

      <p className="mt-4 border-t border-line pt-2 text-[10px] text-ink-soft">
        A scenario, not a forecast.
      </p>
    </div>
  );
}

function Figure({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub: string;
  accent?: boolean;
}) {
  return (
    <div className="rounded-lg border border-line px-2.5 py-2">
      <div className="text-[10px] uppercase tracking-wider text-ink-soft">{label}</div>
      <div
        className={`mt-0.5 text-[15px] font-semibold tabular-nums ${
          accent ? "text-accent" : ""
        }`}
      >
        {value}
      </div>
      <div className="mt-0.5 text-[10px] leading-snug text-ink-soft">{sub}</div>
    </div>
  );
}
